import * as XLSX from "xlsx";

const APPROVED_STATUS_IDS = new Set([2, 4]);

const APPROVER_NAME_FIELDS = [
  "aprobadoPor",
  "AprobadoPor",
  "aprobado_por",
  "aprobado_por_nombre",
  "nombre_aprobador",
  "NombreAprobador",
  "NombreAprobadorSG",
  "usuario_aprobador",
  "UsuarioAprobador",
  "secretariaGeneralAprobadoNombre",
  "secretaria_general_aprobado_nombre",
  "NombreUsuarioSG",
  "usuario_sg",
  "UserSG",
];

const APPROVER_ID_FIELDS = [
  "secretariaGeneralAprobado",
  "SecretariaGeneralAprobado",
  "IdUserSG",
  "idUserSG",
  "id_usuario_aprobado",
  "IdUsuarioAprobado",
  "fk_id_usuario_aprobado",
  "aprobadoPorId",
];

const getValue = (row, fields) => {
  for (const field of fields) {
    const value = row?.[field];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const display = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") return fallback;
  return value;
};

const sanitizeFileName = (value) =>
  String(value || "constancias")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

export const shouldIncludeApprovedBy = (statusId) =>
  APPROVED_STATUS_IDS.has(Number(statusId));

export const fetchUsuariosSecretariaGeneral = async () => {
  try {
    const response = await fetch(
      "https://bei360.injupemp.gob.hn:8444/api/auth/SG/",
    );

    if (!response.ok) throw new Error("No se pudieron obtener usuarios SG");
    return await response.json();
  } catch {
    return [];
  }
};

export const formatConstanciaDate = (value) => {
  if (!value) return "-";

  if (typeof value === "string" && value.includes("T")) {
    const [date, time = ""] = value.split("T");
    return `${date} ${time.substring(0, 8)}`.trim();
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("es-HN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getApproverName = (row, usuariosSG = []) => {
  const directName = getValue(row, APPROVER_NAME_FIELDS);
  if (directName) return directName;

  const approverId = getValue(row, APPROVER_ID_FIELDS);
  if (!approverId) return "No disponible";

  const user = usuariosSG.find((item) => String(item.id) === String(approverId));
  return user?.nombre || approverId;
};

const buildExcelRows = ({
  rows,
  tiposConstancia,
  estadosConstancia,
  includeAprobadoPor,
  usuariosSG,
}) => {
  const hasFechaPagada = rows.some((row) => row.fecha_pagada);
  const hasFechaEmitida = rows.some((row) => row.fecha_emitida);
  const hasObservacionSubsanacion = rows.some((row) => row.Observacion_subsanar);

  return rows.map((row, index) => {
    const excelRow = {
      "#": index + 1,
      "DNI Solicitante": display(row.DNI_usuario),
      Solicitante: display(row.NombreCompleto_usuario),
      "No Recibo": display(row.numero_recibo),
      "Tipo Constancia": display(
        tiposConstancia?.[row.fk_id_constancia] || row.fk_id_constancia,
        "DESCONOCIDO",
      ),
      Monto: display(row.monto),
      Estado: display(
        estadosConstancia?.[row.fk_id_estado] || row.fk_id_estado,
        "DESCONOCIDO",
      ),
      "Fecha Registro": formatConstanciaDate(row.fecha_registro),
    };

    if (hasFechaPagada) {
      excelRow["Fecha Pagado"] = formatConstanciaDate(row.fecha_pagada);
    }

    if (hasFechaEmitida) {
      excelRow["Fecha Emitida"] = formatConstanciaDate(row.fecha_emitida);
    }

    if (includeAprobadoPor) {
      excelRow["Aprobado por"] = getApproverName(row, usuariosSG);
    }

    excelRow["Detalle Solicitud"] = display(row.detalle_solicitud);

    if (hasObservacionSubsanacion) {
      excelRow["Observacion Subsanacion"] = display(row.Observacion_subsanar);
    }

    return excelRow;
  });
};

export const exportConstanciasToExcel = ({
  rows = [],
  bandeja = "Constancias",
  tiposConstancia = {},
  estadosConstancia = {},
  includeAprobadoPor = false,
  usuariosSG = [],
}) => {
  if (!rows.length) return;

  const worksheet = XLSX.utils.json_to_sheet(
    buildExcelRows({
      rows,
      tiposConstancia,
      estadosConstancia,
      includeAprobadoPor,
      usuariosSG,
    }),
  );

  worksheet["!autofilter"] = { ref: worksheet["!ref"] };
  worksheet["!cols"] = [
    { wch: 6 },
    { wch: 18 },
    { wch: 34 },
    { wch: 18 },
    { wch: 32 },
    { wch: 14 },
    { wch: 20 },
    { wch: 22 },
    { wch: 22 },
    { wch: 22 },
    { wch: 28 },
    { wch: 40 },
    { wch: 40 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Constancias");

  const today = new Date().toISOString().slice(0, 10);
  const fileName = `constancias_${sanitizeFileName(bandeja)}_${today}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};
